
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useSwipeable } from 'react-swipeable';
import styles from './EventDetailPage.module.css';
import { useAuth } from '../context/AuthContext';
import EventGalleryImageCard from '../components/EventGalleryImageCard';
import GalleryLightbox from '../components/GalleryLightbox';
import {
  freeRegister,
  getFreeRegisterStatus,
  createDonationCheckout,
  getMyDonations,
} from '../api/events';



const EventDetailPage = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState('');
  const [applyError, setApplyError] = useState('');
  const [quantities, setQuantities] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('基本情報');
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [expandedArtistIndex, setExpandedArtistIndex] = useState(null);
  // 無料参加登録
  const [freeRegStatus, setFreeRegStatus] = useState(null);
  const [freeRegLoading, setFreeRegLoading] = useState(false);
  const [freeRegSubmitting, setFreeRegSubmitting] = useState(false);
  const [freeRegError, setFreeRegError] = useState('');
  // 投げ銭
  const [donationList, setDonationList] = useState([]);
  const [donationLoading, setDonationLoading] = useState(false);
  const [donationAmount, setDonationAmount] = useState('');
  const [donationSubmitting, setDonationSubmitting] = useState(false);
  const [donationError, setDonationError] = useState('');
  const [donationResult, setDonationResult] = useState(null); // 'success' | 'cancel' | null
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const canceledStatuses = new Set([
    'cancellation_pending_payment',
    'canceled',
    'refunding',
    'cancellation_completed',
    'cancellation_failed',
  ]);

  // スワイプハンドラも先頭で定義
  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (event && event.flyers && event.flyers.length > 0) {
        setCurrentIndex((prev) => (prev + 1) % event.flyers.length);
      }
    },
    onSwipedRight: () => {
      if (event && event.flyers && event.flyers.length > 0) {
        setCurrentIndex((prev) => (prev - 1 + event.flyers.length) % event.flyers.length);
      }
    },
    trackMouse: true,
  });

  useEffect(() => {
    let active = true;
    axios.get(`${process.env.REACT_APP_API_URL}/events/${id}`)
      .then((res) => {
        const eventData = res?.data?.event;
        const status = String(eventData?.cancellation_status || 'none');
        if (canceledStatuses.has(status)) {
          const eventPathId = eventData?.slug || id;
          navigate(`/events/${eventPathId}/cancelled`, {
            replace: true,
            state: { eventName: eventData?.name || '' },
          });
          return;
        }
        if (active) setEvent(eventData);
      })
      .catch(() => {
        if (active) setError('イベント情報の取得に失敗しました');
      });

    return () => {
      active = false;
    };
  }, [id, navigate]);

  const {
    name, open_time, start_time, end_time,
    venue, address, description, genre, event_artists, flyers = []
  } = event || {};

  const normalizedDescription = (() => {
    if (description === null || description === undefined) return '';
    if (typeof description !== 'string') return String(description);
    const trimmed = description.trim();
    if (trimmed.toLowerCase() === 'null') return '';
    return description;
  })();

  // 新API: price_groups に対応（旧 prices からの後方互換も維持）
  const priceGroups = Array.isArray(event?.price_groups) ? event.price_groups : null;
  // フラットな価格配列（旧API互換）
  const flatPrices = priceGroups
    ? priceGroups.flatMap(g => (g.prices || []).map(p => ({
        ...p,
        type: g.type || '',
        price_date: g.price_date || null,
        price_end_date: g.price_end_date || null,
      })))
    : (Array.isArray(event?.prices) ? event.prices : []);

  const today = new Date();

  const parseDate = (d) => {
    if (!d) return null;
    let s = String(d);
    // 支持: YYYY-MM-DD, YYYY-MM-DDTHH:mm[:ss], YYYY-MM-DD HH:mm[:ss]
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      s = `${s}T00:00:00`;
    } else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(s)) {
      s = s.replace(' ', 'T');
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) s += ':00';
    }
    const t = new Date(s);
    return isNaN(t) ? null : t;
  };

  // 日付のみの場合は当日の終わり(23:59:59)で比較するためのヘルパー
  const parseDateEOD = (d) => {
    if (!d) return null;
    const s = String(d);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const t = new Date(`${s}T23:59:59`);
      return isNaN(t) ? null : t;
    }
    return parseDate(d);
  };

  const isPurchasable = (price) => {
    const from = parseDate(price.price_date);
    const to = parseDate(price.price_end_date);
    if (from && today < from) return false;
    if (to && today > to) return false;
    return true;
  };

  const priceStatusLabel = (price) => {
    const from = parseDate(price.price_date);
    const to = parseDate(price.price_end_date);
    if (from && today < from) return '販売開始前';
    if (to && today > to) return '販売終了';
    return null;
  };

  const renderTypeLabel = (price) => {
    // Known types handling
    if (price.type === 'vip') return <strong>【VIP】</strong>;
    if (price.type === 'group') return <strong>【グループ（{price.min_people}人～）】</strong>;

    if (price.type === '通常') {
      if (price.gender) return <strong>【{price.gender === 'male' ? '男性' : '女性'}】</strong>;
      return <strong>【通常】</strong>;
    }

    // Fallbacks for free-input values: show type and/or price_type if present
    if (price.price_type) {
      return <span>{String(price.type || '').trim()} {String(price.price_type).trim()}</span>;
    }
    if (price.type) return <span>{String(price.type).trim()}</span>;
    return null;
  };

  const handleApply = () => {
    const items = Object.entries(quantities || {})
      .map(([price_id, qty]) => ({ price_id, quantity: Number(qty) }))
      .filter((i) => i.quantity > 0);
    if (!items.length) {
      setApplyError('申し込みするチケットを選択してください');
      return;
    }
    const totalQty = items.reduce((sum, i) => sum + i.quantity, 0);
    if (totalQty > 10) {
      setApplyError('1回の購入でお申し込みできるのは最大10枚までです');
      return;
    }
    setApplyError('');
    navigate(`/events/${id}/confirm`, { state: { items } });
  };

  const handleFreeRegister = async () => {
    setFreeRegSubmitting(true);
    setFreeRegError('');
    try {
      await freeRegister(event.slug || event.public_id);
      const status = await getFreeRegisterStatus(event.slug || event.public_id);
      setFreeRegStatus(status);
    } catch (err) {
      const msg = err?.response?.data?.message || '参加登録に失敗しました。もう一度お試しください。';
      setFreeRegError(msg);
    } finally {
      setFreeRegSubmitting(false);
    }
  };

  const handleDonationSubmit = async () => {
    const amt = parseInt(donationAmount, 10);
    if (!amt || amt < 500) {
      setDonationError('500円以上で入力してください');
      return;
    }
    if (amt > 50000) {
      setDonationError('50,000円以下で入力してください');
      return;
    }
    setDonationSubmitting(true);
    setDonationError('');
    try {
      const data = await createDonationCheckout(event.slug || event.public_id, amt);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || '決済セッションの作成に失敗しました。';
      setDonationError(msg);
      setDonationSubmitting(false);
    }
  };

  const getEventStartAt = () => {
    if (event?.start_at) return parseDate(event.start_at);
    const datePart = String(event?.date || '').slice(0, 10);
    const startPart = String(start_time || '').slice(0, 8);
    if (datePart && startPart) return parseDate(`${datePart} ${startPart}`);
    if (datePart) return parseDate(`${datePart}T00:00:00`);
    return null;
  };

  const eventStartAt = getEventStartAt();
  const hasEventStarted = !!eventStartAt && today >= eventStartAt;
  const ticketTabLabel = event?.event_type === 'free' ? '参加登録' : 'チケット購入';
  const showDonationTab = !!event?.donation_enabled;
  const tabs = hasEventStarted
    ? ['基本情報', '出演', ticketTabLabel, 'ギャラリー', ...(showDonationTab ? ['投げ銭'] : [])]
    : ['基本情報', '出演', ticketTabLabel, ...(showDonationTab ? ['投げ銭'] : [])];

  useEffect(() => {
    if (!hasEventStarted && activeTab === 'ギャラリー') {
      setActiveTab('基本情報');
    }
  }, [hasEventStarted, activeTab]);

  // クエリパラメータで投げ銭結果を表示
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get('donation_result');
    if (result === 'success' || result === 'cancel') {
      setDonationResult(result);
      setActiveTab('投げ銭');
      // URLをクリーン
      const cleanUrl = location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, [location.search, location.pathname]);

  // 無料参加状態取得
  useEffect(() => {
    if (!event || event.event_type !== 'free' || !user) return;
    let cancelled = false;
    setFreeRegLoading(true);
    getFreeRegisterStatus(event.slug || event.public_id)
      .then((data) => { if (!cancelled) setFreeRegStatus(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setFreeRegLoading(false); });
    return () => { cancelled = true; };
  }, [event, user]);

  // 投げ銭履歴取得
  useEffect(() => {
    if (!event || !user || activeTab !== '投げ銭') return;
    let cancelled = false;
    setDonationLoading(true);
    getMyDonations(event.slug || event.public_id)
      .then((data) => { if (!cancelled) setDonationList(data.donations || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setDonationLoading(false); });
    return () => { cancelled = true; };
  }, [event, user, activeTab]);

  useEffect(() => {
    if (!hasEventStarted || activeTab !== 'ギャラリー') return;
    let cancelled = false;
    setGalleryLoading(true);
    setGalleryError('');

    axios.get(`${process.env.REACT_APP_API_URL}/events/${id}/gallery`, {
      params: { page: 1 },
    })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data;
        const list = Array.isArray(payload?.data)
          ? payload.data
          : (Array.isArray(payload) ? payload : []);
        setGalleryImages(list);
      })
      .catch(() => {
        if (cancelled) return;
        setGalleryImages([]);
        setGalleryError('ギャラリー画像の取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setGalleryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasEventStarted, activeTab, id]);

  const openGalleryLightbox = (index) => {
    if (!galleryImages.length) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (error) return <p>{error}</p>;
  if (!event) return <p>読み込み中...</p>;

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{name}</h1>

      {flyers.length > 0 ? (
        <div className={styles.slider} {...handlers}>
          <img
            src={flyers[currentIndex].url}
            alt="フライヤー"
            className={styles.flyer}
          />
          {flyers.length > 1 && (
            <div className={styles.indicator}>
              {flyers.map((flyer, i) => (
                <span
                  key={flyer.id ?? i}
                  className={i === currentIndex ? styles.dotActive : styles.dot}
                  onClick={() => setCurrentIndex(i)}
                ></span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className={styles.noImage}>No Image</div>
      )}

      {/* Tabs */}
      <div className={styles.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`${styles.tabButton} ${activeTab === tab ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === '基本情報' && (
        <div>
          <div className={styles.infoRow}>
            <div className={styles.eventDate}>
              <strong>開催日：</strong>
              {event.date.slice(0, 10) === event.end_date.slice(0, 10)
                ? event.date.slice(0, 10)
                : `${event.date.slice(0, 10)} 〜 ${event.end_date.slice(0, 10)}`}
            </div>
          </div>
          <div className={styles.infoRow}>
            <strong>時間：</strong>開場 {open_time.slice(0, 5)} / 開演 {start_time.slice(0, 5)} / 終演 {end_time.slice(0, 5)}
          </div>
          {genre && (
            <div className={styles.infoRow}>
              <strong>ジャンル：</strong>{genre}
            </div>
          )}
          <div className={styles.infoRow}>
            <strong>会場：</strong>
            {event.venue_relation ? (
              <Link to={`/profile/${event.venue_relation.user?.slug}`} className={styles.venueLink}>
                {venue}（{address}）
              </Link>
            ) : (
              `${venue}（${address}）`
            )}
            {(() => {
              const lat = event?.venue_relation?.latitude;
              const lng = event?.venue_relation?.longitude;
              const hasCoords = lat !== null && lat !== undefined && lng !== null && lng !== undefined;
              if (!hasCoords) return null;
              const query = encodeURIComponent(`${lat},${lng}`);
              const href = `https://www.google.com/maps/search/?api=1&query=${query}`;
              return (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  <button className={styles.mapButton}>Google Mapで開く</button>
                </a>
              );
            })()}
          </div>

          
          <div className={styles.infoRow}>
            <strong>チケット情報：</strong>
            {(() => {
              const map = new Map();
              flatPrices.forEach((p) => {
                const key = `${p.type}||${p.price_date || ''}||${p.price_end_date || ''}`;
                if (!map.has(key))
                  map.set(key, { type: p.type, price_date: p.price_date, price_end_date: p.price_end_date, items: [] });
                map.get(key).items.push(p);
              });
              return Array.from(map.values()).map((g, gi) => (
                <div key={gi} style={{ marginBottom: '1rem' }}>
                  <div>
                    <strong>{renderTypeLabel(g)}</strong>
                    {g.price_date && (
                      <span> （販売期間: {g.price_date}{g.price_end_date ? `〜${g.price_end_date}` : ''}）</span>
                    )}
                  </div>
                  <ul>
                    {g.items.map((it) => (
                      <li key={it.id}>
                        {it.price_type} — {it.price}円 {it.min_people ? `（${it.min_people}人～）` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ));
            })()}
          </div>

          {normalizedDescription && (
            <div className={styles.infoRow}>
              <strong>イベント説明：</strong>
              <div style={{ whiteSpace: 'pre-line' }}>{normalizedDescription}</div>
            </div>
          )}
          {event?.contact_info && (
            <div className={styles.infoRow}>
              <strong>問い合わせ先：</strong>
              <div style={{ whiteSpace: 'pre-line' }}>{event.contact_info}</div>
            </div>
          )}
        </div>
      )}

      {activeTab === '出演' && (
        <div>
          <h3>出演アーティスト</h3>
          <ul className={styles.artistList}>
            {event_artists.map((artist, idx) => {
              const imgUrl = artist.artist_profile_image_url || null;
              const initials = (artist.artist_name || '').trim().charAt(0).toUpperCase();
              const isCustomArtist = artist.is_registered === false;
              const hasArtistDescription = String(artist.artist_description || '').trim() !== '';
              const Name = (
                artist.artist_slug ? (
                  <Link to={`/profile/${artist.artist_slug}`} className={styles.venueLink}>
                    {artist.artist_name}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className={isCustomArtist && hasArtistDescription ? styles.artistToggle : styles.artistNamePlain}
                    onClick={() => {
                      if (!isCustomArtist || !hasArtistDescription) return;
                      setExpandedArtistIndex((prev) => prev === idx ? null : idx);
                    }}
                    disabled={!isCustomArtist || !hasArtistDescription}
                  >
                    {artist.artist_name}
                  </button>
                )
              );
              return (
                <li key={idx} className={styles.artistItem}>
                  <div className={styles.artistItemMain}>
                    {imgUrl ? (
                      <img src={imgUrl} alt={`${artist.artist_name} avatar`} className={styles.artistAvatar} />
                    ) : (
                      <span className={styles.artistAvatar} aria-hidden="true">{initials || 'A'}</span>
                    )}
                    <div>
                      {Name}
                      {isCustomArtist && hasArtistDescription && (
                        <div className={styles.artistToggleHint}>クリックで説明を表示</div>
                      )}
                    </div>
                  </div>
                  {isCustomArtist && hasArtistDescription && expandedArtistIndex === idx && (
                    <div className={styles.artistDescriptionPanel}>
                      {artist.artist_description}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {activeTab === ticketTabLabel && event?.event_type === 'free' && (
        <div className={styles.purchaseForm}>
          {freeRegLoading ? (
            <p style={{ textAlign: 'center', color: '#6b7280' }}>読み込み中...</p>
          ) : !user ? (
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <p>参加登録にはログインが必要です。</p>
              <button
                className={styles.button}
                onClick={() => navigate(`/login?next=${encodeURIComponent(location.pathname)}`)}
              >
                ログインページへ
              </button>
            </div>
          ) : user.role !== 'buyer' ? (
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <p>主催者や会場、アーティストはチケット購入は購入者アカウントのみ可能です。現在のアカウントでは購入できません。</p>
              <p>必要であれば購入者用アカウントの作成をお願いいたします。</p>
              <button
                className={styles.button}
                onClick={() => navigate('/register')}
              >
                新規登録（購入者）
              </button>
            </div>
          ) : freeRegStatus?.registered ? (
            <div className={styles.registeredBox}>
              <p className={styles.registeredTitle}>✅ 参加登録済みです</p>
              <p className={styles.ticketCode}>チケットコード: <strong>{freeRegStatus.ticket_code}</strong></p>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <p>このイベントは無料でご参加いただけます。</p>
              {freeRegError && <p className={styles.errorText}>{freeRegError}</p>}
              <button
                className={styles.freeRegButton}
                onClick={handleFreeRegister}
                disabled={freeRegSubmitting}
              >
                {freeRegSubmitting ? '登録中...' : '無料で参加登録する'}
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === ticketTabLabel && event?.event_type !== 'free' && (
        <div className={styles.purchaseForm}>
          {(() => {
            const end = parseDateEOD(event.end_date || event.date);
            const eventUpcoming = !end || end >= today;
            if (!eventUpcoming) {
              return (
                <div style={{ textAlign: 'center', padding: '12px', color: '#6b7280' }}>
                  このイベントは終了しました。チケットの購入はできません。
                </div>
              );
            }
            // イベント未終了時の通常フロー
            if (!user) {
              return (
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <p>チケット購入にはログインが必要です。</p>
                  <button
                    className={styles.button}
                    onClick={() => {
                      const next = `${location.pathname}${location.search || ''}`;
                      navigate(`/login?next=${encodeURIComponent(next)}`);
                    }}
                  >
                    ログインページへ
                  </button>
                </div>
              );
            }
            if (user.role !== 'buyer') {
              return (
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <p>チケット購入は購入者アカウントのみ可能です。現在のアカウントでは購入できません。</p>
                  <p>必要であれば購入者用アカウントの作成をお願いいたします。</p>
                  <button
                    className={styles.button}
                    onClick={() => navigate('/register')}
                  >
                    新規登録（購入者）
                  </button>
                </div>
              );
            }

            const purchasable = flatPrices.filter(isPurchasable);
            if (purchasable.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '12px', color: '#6b7280' }}>
                  現在販売中のチケットはありません。
                </div>
              );
            }

            const map = new Map();
            purchasable.forEach((p) => {
              const key = `${p.type}||${p.price_date || ''}||${p.price_end_date || ''}`;
              if (!map.has(key)) map.set(key, { type: p.type, price_date: p.price_date, price_end_date: p.price_end_date, items: [] });
              map.get(key).items.push(p);
            });

            return (
              <>
                <table className={styles.priceTable}>
                  <thead>
                    <tr>
                      <th>種別</th>
                      <th>価格</th>
                      <th>枚数</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(map.values()).map((g, gi) => (
                      <React.Fragment key={gi}>
                        <tr className={styles.priceGroupHeader}>
                          <td colSpan={3}>
                            <strong>{renderTypeLabel(g)}</strong>
                            {g.price_date && (
                              <span> （販売期間: {g.price_date}{g.price_end_date ? `〜${g.price_end_date}` : ''}）</span>
                            )}
                          </td>
                        </tr>
                        {g.items.map((price) => (
                          <tr key={price.id}>
                            <td>
                              {price.price_type} {price.min_people ? <span>（{price.min_people}人～）</span> : null}
                              {priceStatusLabel(price) && (
                                <span style={{ marginLeft: 8, color: '#6b7280' }}>（{priceStatusLabel(price)}）</span>
                              )}
                            </td>
                            <td>{price.price}円</td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                max="10"
                                value={quantities[price.id] ?? ''}
                                disabled={!!priceStatusLabel(price)}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setQuantities((prev) => {
                                    const next = { ...prev };
                                    if (val === '' || val === undefined) {
                                      delete next[price.id];
                                    } else {
                                      const n = Number(val);
                                      next[price.id] = Number.isNaN(n) ? undefined : n;
                                    }
                                    return next;
                                  });
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                {applyError && (
                  <div style={{ color: '#ef4444', marginTop: 8 }}>{applyError}</div>
                )}
                <button className={styles.button} onClick={handleApply}>申し込みへ</button>
              </>
            );
          })()}
        </div>
      )}

      {activeTab === 'ギャラリー' && hasEventStarted && (
        <div className={styles.gallerySection}>
          {galleryError && <p className={styles.galleryError}>{galleryError}</p>}
          {galleryLoading && <p className={styles.galleryLoading}>読み込み中...</p>}
          {!galleryLoading && !galleryError && galleryImages.length === 0 && (
            <p className={styles.galleryMuted}>公開済みのギャラリー画像はまだありません。</p>
          )}
          {!galleryLoading && !galleryError && galleryImages.length > 0 && (
            <div className={styles.galleryGrid}>
              {galleryImages.map((img, idx) => (
                <EventGalleryImageCard
                  key={`detail-gallery-${img.id}`}
                  image={img}
                  onImageClick={() => openGalleryLightbox(idx)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === '投げ銭' && (
        <div className={styles.donationSection}>
          {donationResult === 'success' && (
            <div className={styles.donationSuccess}>
              🎉 投げ銭が完了しました！ありがとうございます。
            </div>
          )}
          {donationResult === 'cancel' && (
            <div className={styles.donationCancel}>
              投げ銭はキャンセルされました。
            </div>
          )}

          {!event?.donation_enabled ? (
            <p style={{ color: '#6b7280' }}>このイベントでは投げ銭を受け付けていません。</p>
          ) : !event?.is_donation_open ? (
            <p style={{ color: '#6b7280' }}>現在投げ銭の受付期間外です。</p>
          ) : (
            <>
              <div className={styles.donationBox}>
                <h3 className={styles.donationTitle}>アーティストを応援する</h3>
                <p className={styles.donationDesc}>
                  投げ銭はアーティストへの応援として届きます（手数料15%）。
                  {event?.donation_end_at && (
                    <span> 受付期間: 〜 {String(event.donation_end_at).slice(0, 10)}</span>
                  )}
                </p>
                {!user ? (
                  <div style={{ textAlign: 'center' }}>
                    <p>投げ銭にはログインが必要です。</p>
                    <button
                      className={styles.button}
                      onClick={() => navigate(`/login?next=${encodeURIComponent(location.pathname)}`)}
                    >
                      ログインページへ
                    </button>
                  </div>
                ) : (
                  <div className={styles.donationForm}>
                    <label className={styles.donationLabel}>金額（500円以上、50,000円以下）</label>
                    <div className={styles.donationAmountRow}>
                      {[500, 1000, 3000, 5000].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          className={`${styles.presetButton} ${donationAmount === String(preset) ? styles.presetActive : ''}`}
                          onClick={() => { setDonationAmount(String(preset)); setDonationError(''); }}
                        >
                          ¥{preset.toLocaleString()}
                        </button>
                      ))}
                    </div>
                    <input
                      type="number"
                      className={styles.donationInput}
                      min="500"
                      max="50000"
                      step="100"
                      placeholder="金額を入力"
                      value={donationAmount}
                      onChange={(e) => { setDonationAmount(e.target.value); setDonationError(''); }}
                    />
                    {donationError && <p className={styles.errorText}>{donationError}</p>}
                    <button
                      className={styles.donationButton}
                      onClick={handleDonationSubmit}
                      disabled={donationSubmitting || !donationAmount}
                    >
                      {donationSubmitting ? '処理中...' : `¥${Number(donationAmount || 0).toLocaleString()} を投げ銭する`}
                    </button>
                  </div>
                )}
              </div>

              {user && donationList.length > 0 && (
                <div className={styles.donationHistory}>
                  <h4 className={styles.donationHistoryTitle}>あなたの投げ銭履歴</h4>
                  <div className={styles.donationTableWrapper}>
                  <table className={styles.donationTable}>
                    <thead>
                      <tr>
                        <th>日時</th>
                        <th>金額</th>
                        <th>状態</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donationList.map((d) => (
                        <tr key={d.public_id}>
                          <td>{String(d.created_at || '').slice(0, 10)}</td>
                          <td>¥{Number(d.amount).toLocaleString()}</td>
                          <td>
                            {d.status === 'paid' ? '✅ 完了' :
                             d.status === 'refunded' ? '↩ 返金済み' : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {donationLoading && <p style={{ color: '#6b7280' }}>読み込み中...</p>}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <GalleryLightbox
        images={galleryImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default EventDetailPage;
